import { escapeHtml } from '../format.js';
import { groupBySection, type MenuPage } from '../paginate.js';
import type { MenuItemContent } from '../types.js';
import {
  badge,
  bilingual,
  description,
  image,
  priceBlock,
  type MenuTemplate,
  type RenderContext,
} from './common.js';

/**
 * Dishes as round plates seen from above, laid out in a grid on a dark table, each with its
 * price on a chip across the rim and the name beneath. Best with top-down photos.
 */

const S = '.mb-t-plate-grid';

function plate(ctx: RenderContext, item: MenuItemContent): string {
  const photo = image(ctx, item.imageMediaId, 'pg-img');
  const initial = escapeHtml((item.title.trim()[0] ?? '?').toUpperCase());
  const badges = badge(ctx, item, 'pg-badge');
  return [
    '<div class="pg-cell">',
    `<div class="pg-plate">${photo || `<span class="pg-initial">${initial}</span>`}`,
    badges ? `<span class="pg-badges">${badges}</span>` : '',
    `<span class="pg-chip">${priceBlock(ctx, item, 'pg')}</span></div>`,
    bilingual(ctx, item.title, item.titleAr, 'pg-name'),
    description(ctx, item, 'pg-desc'),
    '</div>',
  ].join('');
}

export const plateGrid: MenuTemplate = {
  id: 'plate-grid',
  name: 'Plate grid',
  description: 'Round plates seen from above in a grid, each with a price chip and its name. Works for any cuisine with good photos.',
  defaultTheme: {
    backgroundColor: '#1d2023',
    surfaceColor: '#2a2e33',
    textColor: '#f3f3f3',
    primaryColor: '#ffffff',
    accentColor: '#f2b134',
    fontId: 'cafe',
    backgroundMediaId: null,
  },
  variants: [
    { id: 'slate', name: 'Slate', theme: {} },
    { id: 'marble', name: 'Marble', theme: { backgroundColor: '#ecebe7', surfaceColor: '#ffffff', textColor: '#2b2b2b', primaryColor: '#151515', accentColor: '#b5562f', fontId: 'classic' } },
    { id: 'walnut', name: 'Walnut', theme: { backgroundColor: '#3a291d', surfaceColor: '#4a3527', accentColor: '#f4c95d' } },
    { id: 'neon', name: 'Neon', theme: { backgroundColor: '#0b0b10', surfaceColor: '#16161f', accentColor: '#ff3d7f', fontId: 'street' } },
  ],
  cuisines: ['pasta', 'pizza', 'cafe'],
  uses: { featured: false, sectionImages: false, brand: true, decorations: [] },

  paginate(board, orientation) {
    const capacity = orientation === 'portrait' ? 6 : 8;
    const items = groupBySection(board).flatMap((g) => g.items);
    const pages: MenuPage[] = [];
    for (let i = 0; i < items.length; i += capacity) {
      pages.push({ columns: [[{ section: board.sections[0] ?? none, items: items.slice(i, i + capacity), continued: false }]] });
    }
    return pages.length ? pages : [{ columns: [[]] }];
  },

  render(ctx) {
    const { board } = ctx;
    const items = ctx.page.columns.flat().flatMap((b) => b.items);
    const header = [
      '<div class="pg-header">',
      bilingual(ctx, board.settings.title ?? board.name, board.settings.titleAr, 'pg-heading'),
      bilingual(ctx, board.brand.name, board.brand.nameAr, 'pg-brand'),
      '</div>',
    ].join('');
    const portrait = ctx.orientation === 'portrait';
    const cols = Math.min(portrait ? 2 : 4, Math.max(1, items.length));
    return (
      header +
      (items.length
        ? `<div class="pg-grid" style="--cols:${cols};--rows:${Math.ceil(items.length / cols)}">${items.map((i) => plate(ctx, i)).join('')}</div>`
        : '<div class="mb-empty">No dishes on this board yet</div>')
    );
  },

  css: `
${S}{padding:2.6em 3.2em;
  background-image:radial-gradient(circle at 50% 30%,rgba(255,255,255,.06),transparent 60%),
    repeating-linear-gradient(45deg,rgba(255,255,255,.012) 0 .2em,transparent .2em .6em)}
${S}.mb-v-marble{background-image:radial-gradient(ellipse at 20% 10%,rgba(255,255,255,.8),transparent 50%),
  repeating-linear-gradient(115deg,rgba(0,0,0,.025) 0 .1em,transparent .1em 3em)}
${S} .pg-header{display:flex;align-items:baseline;justify-content:center;gap:1.4em;margin-bottom:1.8em}
${S} .pg-heading{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:3.6em;text-transform:uppercase;
  color:var(--mb-primary);letter-spacing:.06em;line-height:1}
${S} .pg-heading.mb-2nd{font-size:2.2em}
${S} .pg-brand{color:var(--mb-accent);font-size:1.1em;letter-spacing:.3em;text-transform:uppercase}
${S} .pg-grid{flex:1;min-height:0;display:grid;gap:1.6em 2.6em;grid-template-columns:repeat(var(--cols),minmax(0,1fr));
  grid-template-rows:repeat(var(--rows),minmax(0,1fr))}
${S} .pg-cell{min-height:0;display:flex;flex-direction:column;align-items:center;text-align:center}
${S} .pg-plate{position:relative;flex:0 1 auto;height:calc(100% - 5.2em);min-height:0;aspect-ratio:1;max-width:100%;border-radius:50%;
  background:var(--mb-surface);box-shadow:0 0 0 .55em color-mix(in srgb,var(--mb-surface) 80%,#fff),0 1.4em 2.6em rgba(0,0,0,.45)}
${S}.mb-v-marble .pg-plate{box-shadow:0 0 0 .55em #fff,0 1.2em 2.2em rgba(0,0,0,.18)}
${S} .pg-img{width:100%;height:100%;object-fit:cover;border-radius:50%;display:block}
${S} .pg-initial{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;font-size:4em;opacity:.3;
  font-family:var(--mb-font-heading)}
${S} .pg-chip{position:absolute;left:50%;bottom:-.6em;transform:translateX(-50%);background:var(--mb-accent);color:var(--mb-bg);
  padding:.3em 1em;border-radius:99em;font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);white-space:nowrap;
  box-shadow:0 .4em 1em rgba(0,0,0,.35)}
${S} .pg-price{font-size:1.45em}
${S} .pg-pricebox{display:flex;align-items:baseline;gap:.5em}
${S} .pg-old{font-size:.9em;opacity:.6}
${S} .pg-sizes{display:flex;gap:.7em}
${S} .pg-size small{font-size:.75em;margin-inline-end:.15em}
${S} .pg-size .pg-price{font-size:1.2em}
${S} .pg-badges{position:absolute;top:4%;inset-inline-end:2%;display:flex;flex-direction:column;gap:.3em}
${S} .pg-badge{background:var(--mb-primary);color:var(--mb-bg);font-weight:800;font-size:.85em;padding:.2em .6em;border-radius:99em}
${S} .pg-name{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:1.5em;color:var(--mb-primary);
  margin-top:1em;line-height:1.1;text-transform:uppercase}
${S} .pg-name.mb-2nd{font-size:1.15em;margin-top:.15em;text-transform:none}
${S} .pg-desc{font-size:.85em;opacity:.7;margin-top:.2em;display:-webkit-box;-webkit-line-clamp:1;-webkit-box-orient:vertical;overflow:hidden}
`,
};

const none = { id: '', title: null, titleAr: null, subtitle: null, subtitleAr: null, imageMediaId: null };
